<template>
  <div class="min-h-screen bg-gray-100 py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex gap-8">
        <div class="hidden lg:block w-64"></div>
        <div class="text-center mb-8 flex-1">
          <h1 class="text-3xl font-bold text-gray-900 sm:text-4xl">Help Center</h1>
          <p class="mt-3 text-xl text-gray-500">Everything you need to know about using our platform</p>
        </div>
      </div>

      <div class="flex gap-8">
        <!-- Sidebar -->
        <div class="hidden lg:block w-64 flex-shrink-0">
          <nav class="sticky top-8">
            <div class="bg-white rounded-lg shadow-md p-4">
              <ul class="space-y-2">
                <li>
                  <button 
                    @click="scrollToSection('orders')"
                    class="w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors"
                    :class="{ 'bg-logo text-white': activeSection === 'orders' || isSubSectionOfOrders, 'text-gray-600 hover:bg-gray-50': activeSection !== 'orders' && !isSubSectionOfOrders }"
                  >
                    Creating Orders
                  </button>
                  <ul 
                    class="ml-4 mt-1 space-y-1"
                    v-show="activeSection === 'orders' || isSubSectionOfOrders"
                  >
                    <li>
                      <button 
                        @click="scrollToSection('basic-usage')"
                        class="w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors"
                        :class="{ 'bg-logo-light text-logo font-medium': activeSection === 'basic-usage', 'text-gray-500 hover:bg-gray-50': activeSection !== 'basic-usage' }"
                      >
                        Basic Usage
                      </button>
                    </li>
                    <li>
                      <button 
                        @click="scrollToSection('csv-upload')"
                        class="w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors"
                        :class="{ 'bg-logo-light text-logo font-medium': activeSection === 'csv-upload', 'text-gray-500 hover:bg-gray-50': activeSection !== 'csv-upload' }"
                      >
                        CSV Upload
                      </button>
                    </li>
                    <li>
                      <button 
                        @click="scrollToSection('payment')"
                        class="w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors"
                        :class="{ 'bg-logo-light text-logo font-medium': activeSection === 'payment', 'text-gray-500 hover:bg-gray-50': activeSection !== 'payment' }"
                      >
                        Payment Process
                      </button>
                    </li>
                  </ul>
                </li>
                <li>
                  <button 
                    @click="scrollToSection('attachments')"
                    class="w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors"
                    :class="{ 'bg-logo text-white': activeSection === 'attachments', 'text-gray-600 hover:bg-gray-50': activeSection !== 'attachments' }"
                  >
                    Managing Attachments
                  </button>
                </li>
                <li>
                  <button 
                    @click="scrollToSection('viewing')"
                    class="w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors"
                    :class="{ 'bg-logo text-white': activeSection === 'viewing', 'text-gray-600 hover:bg-gray-50': activeSection !== 'viewing' }"
                  >
                    Viewing Orders & History
                  </button>
                </li>
                <li>
                  <button 
                    @click="scrollToSection('search')"
                    class="w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors"
                    :class="{ 'bg-logo text-white': activeSection === 'search', 'text-gray-600 hover:bg-gray-50': activeSection !== 'search' }"
                  >
                    Patient Search
                  </button>
                </li>
                <li>
                  <button 
                    @click="scrollToSection('problems')"
                    class="w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors"
                    :class="{ 'bg-logo text-white': activeSection === 'problems', 'text-gray-600 hover:bg-gray-50': activeSection !== 'problems' }"
                  >
                    Managing Problems
                  </button>
                </li>
                <li>
                  <button 
                    @click="scrollToSection('support')"
                    class="w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors"
                    :class="{ 'bg-logo text-white': activeSection === 'support', 'text-gray-600 hover:bg-gray-50': activeSection !== 'support' }"
                  >
                    Support & Contact
                  </button>
                </li>
              </ul>
            </div>
          </nav>
        </div>

        <!-- Main Content -->
        <div class="flex-1 space-y-6">
          <!-- Creating Orders Section -->
          <div id="orders" ref="orders" class="bg-white rounded-lg shadow-md scroll-mt-8">
            <div class="px-6 py-4 bg-logo-dark text-white rounded-t-lg">
              <h2 class="text-xl font-semibold">Creating Orders</h2>
            </div>
            
            <!-- Basic Usage -->
            <div id="basic-usage" ref="basic-usage" class="px-6 py-4 border-b scroll-mt-8">
              <div class="space-y-6">
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">Basic Usage</h3>
                  <div class="mt-4 bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm">
                    <h4 class="font-medium text-gray-900 mb-4">Adding Assignments:</h4>
                    <ol class="ml-4 space-y-3 text-gray-600 list-decimal">
                      <li class="">
                        Fill in the required fields: Patient Name, Document Type, Document Date
                      </li>
                      <li class="">
                        Add any specific instructions in the comments field if needed
                      </li>
                      <li class="">
                        Click the "Add" button to include the assignment in your order
                      </li>
                      <li class="">
                        The system will do a light duplication check to verify that it has not already been added to the current or previous orders.
                        <br>
                        <i class="text-gray-500 p-2">Note: If name, document type, and document date are not all exact matches; it will be added to the order.</i>
                      </li>
                      <li class="">
                        For adding attachments, see the <button @click="scrollToSection('attachments')" class="px-1 text-logo hover:underline">Managing Attachments</button> section
                      </li>
                    </ol>
                  </div>
                  <div class="mt-4 bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm">
                    <h4 class="font-medium text-gray-900 mb-4">Removing Assignments:</h4>
                    <ol class="ml-4 space-y-3 text-gray-600 list-decimal">
                      <li class="">
                        Click the "Attachments / Remove Items" button along the top of the Order Summary pane.
                      </li>
                      <li class="">
                        Click the <i class="fas fa-trash text-red-600"></i> icon in the top-right corner of the assignment you want to remove.
                      </li>
                      <li class="">
                        You may also remove assignments from submitted orders if they have not been completed. See the <button @click="scrollToSection('viewing')" class="px-1 text-logo hover:underline">Viewing Orders & History</button> section for more details.
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- CSV Upload -->
            <div id="csv-upload" ref="csv-upload" class="px-6 py-4 border-b scroll-mt-8">
              <div class="space-y-6">
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">CSV Upload Format</h3>
                  <p class="mt-2 text-gray-600">For multiple assignments, use our CSV upload feature. The file must be in CSV format with the following structure:</p>
                  
                  <div class="mt-6 space-y-8">
                    <!-- Without Headers Format First -->
                    <div>
                      <h4 class="font-medium text-logo-alt2 mb-2">Without Headers (Simple Format)</h4>
                      <p class="text-gray-600 mb-4">When not using headers, columns must be in this exact order: Name, Document Type, Document Date, Comments (optional)</p>
                      <div class="overflow-x-auto">
                        <table class="min-w-full border border-gray-200">
                          <tbody class="bg-white">
                            <tr>
                              <td class="px-4 py-2 border text-gray-600">Mary Wilson</td>
                              <td class="px-4 py-2 border text-gray-600">Recert</td>
                              <td class="px-4 py-2 border text-gray-600">3/1/24</td>
                              <td class="px-4 py-2 border text-gray-600">Monthly check</td>
                            </tr>
                            <tr class="bg-gray-50">
                              <td class="px-4 py-2 border text-gray-600">Tom Brown</td>
                              <td class="px-4 py-2 border text-gray-600">FU</td>
                              <td class="px-4 py-2 border text-gray-600">March 1 2024</td>
                              <td class="px-4 py-2 border text-gray-600">Weekly follow-up</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <!-- With Headers Format Second -->
                    <div class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                      <h4 class="font-medium text-logo-alt2 px-4 pt-4 mb-2">With Headers</h4>
                      <p class="text-gray-600 px-4 mb-4">When using headers, the system looks for specific keywords to identify each column. The columns can be in any order as long as they contain the required keywords:</p>
                      <div class="px-4 mb-4 bg-gray-50 p-3 rounded">
                        <ul class="space-y-2 text-gray-600">
                          <li><strong>Patient Name:</strong> Must contain "patient", "name", or "pt"</li>
                          <li><strong>Document Type:</strong> Must contain "type", "doc", or "visit"</li>
                          <li><strong>Document Date:</strong> Must contain "date"</li>
                          <li><strong>Comments:</strong> Optional - recognized by "comment" or "note"</li>
                        </ul>
                      </div>
                      <table class="min-w-full border border-gray-200">
                        <thead class="bg-gray-50">
                          <tr>
                            <th class="px-4 py-2 border text-sm font-semibold text-gray-900">Patient Name</th>
                            <th class="px-4 py-2 border text-sm font-semibold text-gray-900">Document Type</th>
                            <th class="px-4 py-2 border text-sm font-semibold text-gray-900">Visit Date</th>
                            <th class="px-4 py-2 border text-sm font-semibold text-gray-900">Notes</th>
                          </tr>
                        </thead>
                        <tbody class="bg-white">
                          <tr>
                            <td class="px-4 py-2 border text-gray-600">John Smith</td>
                            <td class="px-4 py-2 border text-gray-600">SOC</td>
                            <td class="px-4 py-2 border text-gray-600">01/15/2024</td>
                            <td class="px-4 py-2 border text-gray-600">Initial assessment</td>
                          </tr>
                          <tr class="bg-gray-50">
                            <td class="px-4 py-2 border text-gray-600">Jane Doe</td>
                            <td class="px-4 py-2 border text-gray-600">Start of Care</td>
                            <td class="px-4 py-2 border text-gray-600">Feb 20 2024</td>
                            <td class="px-4 py-2 border text-gray-600">Post-surgery</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Payment Process -->
            <div id="payment" ref="payment" class="px-6 py-4 scroll-mt-8">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Payment Process</h3>
              <div class="bg-gray-50 p-6 rounded-lg border border-gray-200 shadow-sm">
                <ul class="ml-4 space-y-3 text-gray-600">
                  <li class="flex items-start">
                    <span class="mr-2">1.</span>
                    When you submit an order, we place a hold on your card for the total amount
                  </li>
                  <li class="flex items-start">
                    <span class="mr-2">2.</span>
                    This hold reserves the funds but does not charge your card
                  </li>
                  <li class="flex items-start">
                    <span class="mr-2">3.</span>
                    The actual charge only occurs after your coding is completed
                  </li>
                  <li class="flex items-start">
                    <span class="mr-2">4.</span>
                    If any issues arise or changes are needed, the hold amount can be adjusted
                  </li>
                  <li class="flex items-start">
                    <span class="mr-2">5.</span>
                    You'll receive a notification when the coding is complete and the charge is processed
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Managing Attachments Section (New Parent Section) -->
          <div id="attachments" ref="attachments" class="bg-white rounded-lg shadow-md scroll-mt-8">
            <div class="px-6 py-4 bg-logo-dark text-white rounded-t-lg">
              <h2 class="text-xl font-semibold">Managing Attachments</h2>
            </div>
            
            <div class="px-6 py-4">
              <div class="bg-gray-50 p-6 rounded-lg border border-gray-200 shadow-sm space-y-8">
                <!-- During Order Creation -->
                <div>
                  <h4 class="font-medium text-gray-900 mb-4">Adding Attachments During Order Creation</h4>
                  <ul class="ml-4 space-y-3 text-gray-600">
                    <li class="flex items-start">
                      <span class="mr-2">1.</span>
                      <div>
                        Adding attachments is done after you have added the assignment to the order, but before you have submitted the order.
                      </div>
                    </li>
                    <li class="flex items-start">
                      <span class="mr-2">2.</span>
                      Click "Attachments / Remove Items" to allow adding and removing attachments to any assignment in the order.
                    </li>
                    <li class="flex items-start">
                      <span class="mr-2">3.</span>
                      Click the <i class="p-1 fas fa-paperclip text-logo-alt2"></i> icon for the assignment that you want to add attachments to.
                    </li>
                    <li class="flex items-start">
                      <span class="mr-2">4.</span>
                      <div>
                        A "dropzone" will appear where you can drag and drop your attachments or click "Click to upload" to upload file(s).
                        <p class="text-gray-600 text-sm">- You may upload multiple files at once using ctrl/cmd key.</p>
                      </div>
                    </li>
                  </ul>
                </div>

                <!-- After Order Submission -->
                <div>
                  <h4 class="font-medium text-gray-900 mb-4">Managing Attachments After Order Submission</h4>
                  <ul class="ml-4 space-y-3 text-gray-600">
                    <li class="flex items-start">
                      <span class="mr-2">1.</span>
                      <div>
                        You can add or remove attachments from the order details page as long as the assignment hasn't been completed.
                        <p class="text-gray-600 text-sm">- Once an assignment is completed, attachments cannot be modified as this would require recoding.</p>
                      </div>
                    </li>
                    <li class="flex items-start">
                      <span class="mr-2">2.</span>
                      <div>
                        Navigate to your order details page:
                          <ul class="ml-6 mt-2 space-y-2 list-disc text-gray-600">
                            <li>By clicking "View Orders" from the top navigation or from your dashboard.</li>
                            <li>Click on the order you want to add/remove attachments for.</li>
                          </ul>
                      </div>
                    </li>
                    <li class="flex items-start">
                      <span class="mr-2">3.</span>
                      <div>
                        Each assignment will show its current attachments and allow you to:
                        <ul class="ml-6 mt-2 space-y-2 list-disc text-gray-600">
                          <li>Add new attachments by clicking the '+' icon in the attachments section
                            <ul class="ml-6 mt-2 space-y-2 list-disc text-gray-600">
                              <li>See Adding Attachments During Order Creation for more details of working with the dropzone file uploader.</li>
                            </ul>

                          </li>
                          <li>Remove existing attachments by hovering over the attachment and clicking the 'x' along the right side.</li>
                        </ul>
                      </div>
                    </li>
                  </ul>
                </div>

                <!-- For Problem Assignments -->
                <div>
                  <h4 class="font-medium text-gray-900 mb-4">Managing Attachments for Problem Assignments</h4>
                  <ul class="ml-4 space-y-3 text-gray-600">
                    <li class="flex items-start">
                      <span class="mr-2">1.</span>
                      When an assignment is marked as having a problem, you can manage attachments from the Problems page.
                    </li>
                    <li class="flex items-start">
                      <span class="mr-2">2.</span>
                      Click the "Show Attachments" button to expand the attachments section.
                    </li>
                    <li class="flex items-start">
                      <span class="mr-2">3.</span>
                      <div>
                        You can:
                        <ul class="ml-6 mt-2 space-y-2 list-disc text-gray-600">
                          <li>View current attachments</li>
                          <li>Add new attachments to help resolve the problem</li>
                          <li>Remove incorrect or outdated attachments</li>
                        </ul>
                      </div>
                    </li>
                    <li class="flex items-start">
                      <span class="mr-2">4.</span>
                      After updating attachments, you can add a resolution comment explaining the changes made.
                    </li>
                  </ul>
                </div>

                <!-- General Notes -->
                <div>
                  <h4 class="font-medium text-gray-900 mb-4">Important Notes About Attachments</h4>
                  <ul class="ml-4 space-y-3 text-gray-600">
                    <li class="flex items-start list-disc text-gray-600">
                      We support any file type, but recommend using PDF or TIFF files for best compatibility.
                    </li>
                    <li class="flex items-start list-disc text-gray-600">
                      Attachments cannot be modified after an assignment is completed.
                    </li>
                    <li class="flex items-start list-disc text-gray-600">
                      If you need to modify attachments for a completed assignment, contact support for assistance.
                    </li>
                    <li class="flex items-start list-disc text-gray-600">
                      Always ensure all necessary documentation is attached before marking a problem as resolved.
                    </li>
                  </ul>
                </div>
              </div>
            </div>

          </div>

          <!-- Viewing Orders Section -->
          <div id="viewing" ref="viewing" class="bg-white rounded-lg shadow-md scroll-mt-8">
            <div class="px-6 py-4 bg-logo-dark text-white rounded-t-lg">
              <h2 class="text-xl font-semibold">Viewing Orders & History</h2>
            </div>
            <div class="px-6 py-4">
              <div class="bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm">
                <div class="space-y-4">
                  <h3 class="text-lg font-semibold text-gray-900">Order Management</h3>
                  <ol class="ml-4 space-y-3 text-gray-600 list-decimal">
                    <li class="">
                      Access your complete order history from:
                      <ol class="ml-4 list-alpha">
                        <li class="">The "View Orders" button in your dashboard or navigation</li>
                        <li class="">The Patient Search and clicking the corresponding order in the search results</li>
                      </ol>
                    </li>
                    <li class="">
                      Click on any order to view full details including attachments and coding status
                      <ol class="ml-4 list-disc">
                        <li class="">
                          Track the progress of your orders in real-time
                        </li>
                        <li class="">
                          If your EMR does not allow our coder's to directly input codes, they will be viewable here.
                        </li>
                        <li class="">
                          If an assignment has not been completed or currently being coded, you may:
                          <ul class="ml-6 mt-2 space-y-2 list-disc text-gray-600">
                            <li>Delete the assignment</li>
                            <li>Move the assignment to a "new order", this will create a new order or if you have an existing unsubmitted order, it will be added to that order.
                              <ul class="ml-6 mt-2 space-y-2 list-disc text-gray-600">
                                <li>The cost of the assignment will be deducted from the current order and will be noted in the final charge amount when the order is completed.</li>
                              </ul>
                            </li>
                          </ul>
                        </li>
                        <li class="">
                          You may also move an assignment to a "new order" if it has not been completed or currently being coded.
                        </li>
                      </ol>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Patient Search Section -->
          <div id="search" ref="search" class="bg-white rounded-lg shadow-md scroll-mt-8">
            <div class="px-6 py-4 bg-logo-dark text-white rounded-t-lg">
              <h2 class="text-xl font-semibold">Patient Search</h2>
            </div>
            <div class="px-6 py-4">
              <div class="bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm">
                <div class="space-y-4">
                  <h3 class="text-lg font-semibold text-gray-900">Search Features</h3>
                  <ol class="ml-4 space-y-3 text-gray-600 list-decimal">
                    <li class="">
                      <div>
                        Ways To Search:
                        <ul class="ml-6 mt-2 space-y-2 list-disc">
                          <li>From the top navigation, click "Search" or <i class="fas fa-search text-logo-alt2"></i> icon</li>
                          <li>From the "View Orders" page, use the search bar along the top of the page</li>
                        </ul>
                      </div>
                    </li>
                    <li class="">
                      Search by patient name (case-insensitive)
                    </li>
                    <li class="">
                      Patient, Document Type, Document Date, and Order ID will display in the results panel and you may click on the order to view it.
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Managing Problems Section -->
          <div id="problems" ref="problems" class="bg-white rounded-lg shadow-md scroll-mt-8">
            <div class="px-6 py-4 bg-logo-dark text-white rounded-t-lg">
              <h2 class="text-xl font-semibold">Managing Problems</h2>
            </div>
            
            <div class="px-6 py-4">
              <div class="space-y-6">
                <!-- Understanding Problem Files -->
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">Understanding Problem Files</h3>
                  <div class="mt-4 bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm">
                    <p class="text-gray-600 mb-4">When a coder encounters issues with an assignment, they may mark it as a "Problem File". This could be due to:</p>
                    <ul class="ml-4 space-y-2 text-gray-600 list-disc">
                      <li>Missing or incomplete documentation</li>
                      <li>Unclear or conflicting information</li>
                      <li>Technical issues with attachments</li>
                      <li>Need for additional clarification</li>
                    </ul>
                  </div>
                </div>

                <!-- Notification Process -->
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">Notification Process</h3>
                  <div class="mt-4 bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm">
                    <ol class="ml-4 space-y-3 text-gray-600 list-decimal">
                      <li>When an assignment is marked as a problem, you'll receive an email notification</li>
                      <li>The assignment will be visible in your Problems Management Panel</li>
                      <li>You can view the coder's comments explaining the issue</li>
                    </ol>
                  </div>
                </div>

                <!-- Resolving Problems -->
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">Resolving Problems</h3>
                  <div class="mt-4 bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm">
                    <ol class="ml-4 space-y-3 text-gray-600 list-decimal">
                      <li>
                        Access the Problems Management Panel to view all problem assignments
                      </li>
                      <li>
                        For each problem file, you can:
                        <ul class="ml-6 mt-2 space-y-2 list-disc">
                          <li>Review the coder's comments explaining the issue</li>
                          <li>View and manage attachments</li>
                          <li>Add a resolution comment explaining what was fixed</li>
                          <li>Mark the problem as resolved once addressed</li>
                        </ul>
                      </li>
                      <li>
                        Managing attachments for problem files:
                        <ul class="ml-6 mt-2 space-y-2 list-disc">
                          <li>Click "Show Attachments" to view current attachments</li>
                          <li>Add new attachments to resolve the issue</li>
                          <li>Remove incorrect or outdated attachments</li>
                        </ul>
                      </li>
                      <li>
                        After updating attachments or providing clarification:
                        <ul class="ml-6 mt-2 space-y-2 list-disc">
                          <li>Add a resolution comment explaining the changes made</li>
                          <li>Click "Mark as Resolved" to return the assignment for coding</li>
                        </ul>
                      </li>
                    </ol>
                  </div>
                </div>

                <!-- Important Notes -->
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">Important Notes</h3>
                  <div class="mt-4 bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm">
                    <ul class="ml-4 space-y-3 text-gray-600 list-disc">
                      <li>Resolution comments are required</li>
                      <li>The original coder will be notified when you resolve the problem</li>
                      <li>Problems should be resolved promptly to avoid delays in coding completion</li>
                      <li>If you need additional assistance, contact our <button @click="scrollToSection('support')" class="px-1 text-logo hover:underline">support team</button></li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Support Section -->
          <div id="support" ref="support" class="bg-white rounded-lg shadow-md scroll-mt-8">
            <div class="px-6 py-4 bg-logo-dark text-white rounded-t-lg">
              <h2 class="text-xl font-semibold">Support & Contact</h2>
            </div>
            <div class="px-6 py-4">
              <div class="bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm">
                <div class="space-y-4">
                  <h3 class="font-semibold text-gray-900">Contact Information</h3>
                  <div class="space-y-2 text-gray-600">
                    <p>Our support team is available Monday through Friday, 8 AM to 5 PM GMT <span class="text-gray-600 italic">Mountain Time</span>, (10 AM to 7 PM EST)</p>
                    <p><strong>Phone:</strong> (720) 772-1583</p>
                    <p><strong>Email:</strong> admin@hhcoder.com</p>
                  </div>

                  <!-- <h3 class="font-semibold text-gray-900 pt-4">Common Issues</h3>
                  <ul class="ml-4 space-y-2 text-gray-600">
                    <li class="flex items-start list-disc text-gray-600">
                      Check system status for any known issues
                    </li>
                    <li class="flex items-start list-disc text-gray-600">
                      Review common solutions in our FAQ
                    </li>
                    <li class="flex items-start list-disc text-gray-600">
                      Submit bug reports or feature requests through our support system
                    </li>
                  </ul> -->
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      activeSection: 'orders'
    }
  },
  computed: {
    isSubSectionOfOrders() {
      return ['basic-usage', 'csv-upload', 'payment'].includes(this.activeSection)
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
    this.$nextTick(() => {
      if (window.location.hash) {
        const section = window.location.hash.substring(1)
        this.scrollToSection(section)
      }
    })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    scrollToSection(section) {
      const el = this.$refs[section]
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
        this.activeSection = section
        history.pushState(null, null, `#${section}`)
      }
    },
    handleScroll() {
      const sections = [
        'orders',
        'basic-usage',
        'csv-upload',
        'payment',
        'attachments',
        'viewing',
        'search',
        'support'
      ]
      
      let maxVisibility = 0
      let mostVisibleSection = 'orders'

      sections.forEach(section => {
        const el = this.$refs[section]
        if (el) {
          const rect = el.getBoundingClientRect()
          const visibility = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)
          if (visibility > maxVisibility) {
            maxVisibility = visibility
            mostVisibleSection = section
          }
        }
      })

      // Don't change parent section when viewing subsections
      if (this.isSubSectionOfOrders && ['basic-usage', 'csv-upload', 'payment'].includes(mostVisibleSection)) {
        this.activeSection = mostVisibleSection
      } else if (!this.isSubSectionOfOrders) {
        this.activeSection = mostVisibleSection
      }
    }
  }
}
</script>

<style>
.scroll-mt-8 {
  scroll-margin-top: 2rem;
}
</style> 